/* eslint-disable no-negated-condition */
import type { LyricsEntry, LyricsRowData, LyricsRenderConfig } from '@/data/Lyrics';
import type { Image3DSyncFormat } from '@/formats/UnitParser';
import type { ImageDimensions } from '@/data/sources/Source';
import type { Pixel } from '@/data';
import Staff from '@/data/Staff';

type LyricRow = {
  labels: LyricsEntry[];
  startOnset: number;
  endOnset: number;
  y: number;
  textWidth: number;
};

export class LyricsService {
  private rawLabels: LyricsEntry[];

  private calculatedPositions!: Image3DSyncFormat;

  private totalHeight = 0;

  private width: number;

  private calculatedRenderRows: LyricsRowData[] = [];

  private sourceStaves: Staff[] | undefined;

  private isLinear: boolean;

  private isCentered: boolean;

  private canvasContext = typeof document !== 'undefined'
    ? document.createElement('canvas').getContext('2d')
    : null;

  private rowHeight = 100;

  private fontSize = 24;

  private letterSpacing = 1;

  private wordSpacing = 1;

  private offset = this.fontSize / 4;

  constructor(
    labels: LyricsEntry[],
    sourceStaves?: Staff[],
    width = 1600,
    isCentered = true,
    isLinear = false,
  ) {
    this.width = width;
    this.rawLabels = labels;
    this.sourceStaves = sourceStaves;
    this.isCentered = isCentered;
    this.isLinear = isLinear;
  }

  private getTextWidth(text: string): number {
    let width = 0;
    if (this.canvasContext) {
      this.canvasContext.font = `${this.fontSize}px sans-serif`;
      width = this.canvasContext.measureText(text).width;
    } else {
      width = text.length * (this.fontSize * 0.5);
    }
    if (text.length > 1) { width += (text.length - 1) * this.letterSpacing; }
    const nbSpace = (text.match(/ /g) || []).length;
    if (nbSpace > 0) {
      width += nbSpace * this.wordSpacing;
    }

    return width;
  }

  private createRowStaves(rowY: Pixel): Staff[] {
    if (!this.sourceStaves || this.sourceStaves.length === 0) {
      return [new Staff(0, rowY, (rowY + this.rowHeight) as Pixel, 'all')];
    }
    const stavesPerRow = this.sourceStaves.length;
    const staffHeightInRow = this.rowHeight / stavesPerRow;
    const rowStaves: Staff[] = [];
    for (let i = 0; i < this.sourceStaves.length; i += 1) {
      const sourceStaff = this.sourceStaves[i];
      const staffTop = (rowY + i * staffHeightInRow) as Pixel;
      const staffBottom = (rowY + (i + 1) * staffHeightInRow) as Pixel;
      rowStaves.push(
        new Staff(sourceStaff.id, staffTop, staffBottom, sourceStaff.name, sourceStaff.type),
      );
    }
    return rowStaves;
  }

  private buildRows(): LyricRow[] {
    const rows: LyricRow[] = [];
    let currentLyrics: LyricsEntry[] = [];
    let currentOnset = 0;
    let rowIndex = 0;

    const firstLyric = this.rawLabels.find((l) => l.type === 'lyrics');
    if (firstLyric) {
      currentOnset = firstLyric.start;
    }

    this.rawLabels.forEach((label) => {
      if (!this.isLinear && label.type === 'break') {
        let breakOnset = label.start;

        if (currentLyrics.length > 0) {
          const lastLabelOnset = currentLyrics[currentLyrics.length - 1].start;
          breakOnset = Math.max(breakOnset, lastLabelOnset);
        }

        const textWidth = currentLyrics.reduce((acc, l) => acc + this.getTextWidth(l.tag), 0);

        rows.push({
          labels: currentLyrics,
          startOnset: currentOnset,
          endOnset: breakOnset,
          y: rowIndex * this.rowHeight,
          textWidth,
        });

        rowIndex += 1;
        currentOnset = breakOnset;
        currentLyrics = [];
      } else if (label.type === 'lyrics') {
        currentLyrics.push(label);
      }
    });

    if (currentLyrics.length > 0) {
      const textWidth = currentLyrics.reduce((acc, l) => acc + this.getTextWidth(l.tag), 0);
      const lastLabelOnset = currentLyrics[currentLyrics.length - 1].start;
      rows.push({
        labels: currentLyrics,
        startOnset: currentOnset,
        endOnset: lastLabelOnset + 1.0,
        y: rowIndex * this.rowHeight,
        textWidth,
      });
    }

    return rows;
  }

  public loadAndRender(): void {
    const rows = this.buildRows();

    const unitPages: Image3DSyncFormat = { pages: [{ image: '', rows: [] }] };
    const renderRows: LyricsRowData[] = [];

    rows.forEach((row) => {
      const textY = row.y + (this.rowHeight / 2);
      const startX = this.isCentered ? Math.max(0, (this.width - row.textWidth) / 2) : 30;
      const endX = startX + row.textWidth;
      const rowStartX = startX + this.offset;
      const rowEndX = endX + this.offset;

      let currentX = startX;

      const renderRow: LyricsRowData = {
        y: textY,
        renders: [],
      };

      const onsetX: { onset: number, x: number }[] = [];
      onsetX.push({ onset: row.startOnset, x: rowStartX });

      row.labels.forEach((label) => {
        const xPosition = currentX;

        renderRow.renders.push({
          text: label.tag,
          x: xPosition,
          onset: label.start,
        });

        onsetX.push({ onset: label.start, x: xPosition + this.offset });
        currentX += this.getTextWidth(label.tag);
      });

      renderRows.push(renderRow);

      let finalOnset = row.endOnset;
      if (finalOnset <= row.labels[row.labels.length - 1].start) {
        finalOnset = row.labels[row.labels.length - 1].start + 1.0;
      }
      onsetX.push({ onset: finalOnset, x: rowEndX });

      unitPages.pages[0].rows.push({
        'onset-x': onsetX,
        'start-x': rowStartX as Pixel,
        'end-x': rowEndX as Pixel,
        y: row.y as Pixel,
        height: this.rowHeight,
        staves: this.createRowStaves(row.y as Pixel),
      });
    });

    this.totalHeight = rows.length * this.rowHeight;
    this.calculatedRenderRows = renderRows;
    this.calculatedPositions = unitPages;
  }

  get positions(): Image3DSyncFormat {
    return this.calculatedPositions;
  }

  get dimensions(): ImageDimensions {
    return {
      width: this.width as Pixel,
      height: this.totalHeight as Pixel,
    };
  }

  get renderRows(): LyricsRowData[] {
    return this.calculatedRenderRows;
  }

  get renderConfig(): LyricsRenderConfig {
    return {
      fontSize: this.fontSize,
      letterSpacing: this.letterSpacing,
      wordSpacing: this.wordSpacing,
    };
  }
}
